import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSpotify, FaYoutube, FaWhatsapp } from "react-icons/fa";
import { WHATSAPP_URL } from "@/lib/whatsapp";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowWhatsapp(window.scrollY > window.innerHeight * 0.85);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "#hero" },
    { name: "Hotels & Events", href: "#contact" },
    { name: "Residential", href: "#contact" },
    { name: "About", href: "#about" },
    { name: "Music", href: "#music" },
    { name: "Contact", href: "#contact" },
  ];

  const scrollTo = (href: string) => {
    setIsMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = "/" + href;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="/"
            onClick={(e) => { if (window.location.pathname !== "/") return; e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="font-playfair text-sm md:text-xl tracking-[0.25em] text-white font-normal whitespace-nowrap"
          >
            OLIVIA FLEMMING
          </a>

          <nav className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => scrollTo(item.href)}
                className="font-playfair text-xs uppercase tracking-[0.15em] text-white/75 hover:text-gold transition-colors"
              >
                {item.name}
              </button>
            ))}
            <span className="flex items-center gap-3 pl-2">
              <a href="https://open.spotify.com/artist/3iP5TKieYruTr7XClnpjX1" target="_blank" rel="noopener noreferrer" aria-label="Spotify" className="text-white/75 hover:text-gold"><FaSpotify className="w-4 h-4" /></a>
              <a href="https://www.youtube.com/@oliviaflemmingmusic" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-white/75 hover:text-gold"><FaYoutube className="w-4 h-4" /></a>
            </span>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-hidden={!showWhatsapp} tabIndex={showWhatsapp ? 0 : -1} className={`flex items-center gap-2 bg-cherry/80 border border-gold/50 text-white px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] hover:bg-cherry transition-all duration-500 ease-out ${showWhatsapp ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}>
              <FaWhatsapp className="w-4 h-4" /> Message me
            </a>
          </nav>

          <div className="flex items-center gap-2">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Message me on WhatsApp" aria-hidden={!showWhatsapp} tabIndex={showWhatsapp ? 0 : -1} className={`lg:hidden flex items-center gap-1.5 bg-cherry/80 border border-gold/50 text-white px-3 py-2 rounded-full text-[10px] uppercase tracking-[0.08em] whitespace-nowrap transition-all duration-500 ease-out ${showWhatsapp ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}>
              <FaWhatsapp className="w-4 h-4 shrink-0" /> Message me
            </a>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-white"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 border-t border-white/10"
          >
            <nav className="flex flex-col px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => scrollTo(item.href)}
                  className="font-inter text-lg py-2 text-white/80 hover:text-primary transition-colors text-left"
                >
                  {item.name}
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
